import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import NavBar from './NavBar';
import wiproLogo from './assets/wipro.png';
import './TicketDetailsPage.css';
import { FaArrowLeft, FaFileAlt, FaCheck, FaSave, FaSpinner } from 'react-icons/fa';
import { searchResolutionSteps } from './services/ragService';

const generateSampleData = () => {
  const statuses = ['Open', 'In Progress', 'Resolved'];
  const assignmentGroups = ['Network Team', 'App Support', 'Security'];
  const users = ['Alice', 'Bob', 'Charlie'];
  const descriptions = ['Email issue', 'VPN not working', 'Access denied', 'System crash', 'Password reset'];
  const items = ['Laptop', 'VPN', 'Email', 'Software', 'Access'];
  const priorities = ['High', 'Medium', 'Low'];

  return Array.from({ length: 20 }, (_, i) => {
    const index = i + 1;
    return {
      ticketId: `INC${index.toString().padStart(4, '0')}`,
      shortDescription: descriptions[i % descriptions.length],
      assignmentGroup: assignmentGroups[i % assignmentGroups.length],
      receivedOn: `2025-06-${String(i % 30 + 1).padStart(2, '0')}`,
      assignedTo: users[i % users.length],
      status: statuses[i % statuses.length],
      item: items[i % items.length],
      priority: priorities[i % priorities.length],
      action: 'Review'
    };
  });
};

const TicketDetailsPage = ({ userName, userEmail, onLogout }) => {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState({})

  // RAG API states
  const [ragResolutionSteps, setRagResolutionSteps] = useState([]);
  const [relatedResources, setRelatedResources] = useState([]);
  const [ragLoading, setRagLoading] = useState(false);
  const [ragError, setRagError] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedSteps, setSelectedSteps] = useState(new Set());
  const [activeTab, setActiveTab] = useState('similar');

  // Feedback states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // User Query states
  const [userQuery, setUserQuery] = useState('');
  const [showUserQueryInput, setShowUserQueryInput] = useState(false);
  const [userQueryLoading, setUserQueryLoading] = useState(false);

  useEffect(() => {
    const API_URL = `/api/tickets/${ticketId}`
    
    console.log('Fetching ticket:', ticketId);
    console.log('API URL:', API_URL);

    fetch(API_URL, { method: "GET" })
      .then(async (res) => {
        console.log('Response status:', res.status);
        const data = await res.json();
        console.log('Ticket data received:', data);
        setTicket(data)
      }).catch((error) => {
        console.error('Error fetching ticket:', error);
      })
  }, [ticketId])

  // Fetch RAG resolution steps when ticket is loaded
  useEffect(() => {
    if (ticket && ticket.shortDescription) {
      // Combine short description and description for better context
      const combinedQuery = ticket.description 
        ? `${ticket.shortDescription}\n\n${ticket.description}`
        : ticket.shortDescription;
      
      fetchRAGResolutions(combinedQuery);
    }
  }, [ticket]);

  const fetchRAGResolutions = async (query) => {
    setRagLoading(true);
    setRagError(null);
    
    try {
      const response = await searchResolutionSteps(query);
      
      // Extract resolution steps from LLM
      const steps = response.resolution_steps || [];
      setRagResolutionSteps(steps);
      
      // Convert steps to formatted text for recommendations textarea
      const formattedRecommendations = steps.map((step, index) => `${index + 1}. ${step.replace(/^[-•*]\s*/, '')}`).join('\n\n');
      setRecommendations(formattedRecommendations);
      
      // Keep resolution notes empty
      setResolutionNotes('');
      
      // Store related resources for the Related Resources section (remove duplicates)
      const resources = response.related_resources || [];
      const uniqueResources = [];
      const seenFilenames = new Set();
      
      resources.forEach(resource => {
        if (!seenFilenames.has(resource.filename)) {
          seenFilenames.add(resource.filename);
          uniqueResources.push(resource);
        }
      });
      
      setRelatedResources(uniqueResources);
      
      // Store similar tickets for reference
      setSimilarTickets(response.similar_items || []);
      
    } catch (error) {
      console.error('Error fetching RAG resolutions:', error);
      setRagError('Failed to load AI recommendations. Please try again.');
    } finally {
      setRagLoading(false);
    }
  };

  const handleStepToggle = (index, stepText) => {
    const newSelected = new Set(selectedSteps);
    
    if (newSelected.has(index)) {
      // Remove step from resolution notes
      newSelected.delete(index);
      setResolutionNotes(prev => prev.replace(stepText + '\n', ''));
    } else {
      // Add step to resolution notes
      newSelected.add(index);
      setResolutionNotes(prev => prev + stepText + '\n');
    }
    
    setSelectedSteps(newSelected);
  };

  const [resolutionNotes, setResolutionNotes] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [state, setState] = useState('In Progress');
  const [resolutionCode, setResolutionCode] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [onHoldReason, setOnHoldReason] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [workNotes, setWorkNotes] = useState('');
  const [problemDetails, setProblemDetails] = useState('');
  const [vendorTicket, setVendorTicket] = useState('');

  const recommendationTexts = {
    investigate: 'Investigated the issue thoroughly and reviewed logs.',
    fixes: 'Applied necessary fixes including updates and configuration changes.',
    restart: 'Restarted SQL Server to apply changes and verify resolution.'
  };

  // Feedback handlers
  const handleThumbsUp = async () => {
    console.log('Thumbs up clicked for ticket:', ticketId);
    
    try {
      // Send positive feedback to backend
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'thumbs_up',
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          reason: '',
          comments: ''
        })
      });
      
      const result = await response.json();
      console.log('Feedback stored:', result);
      
      setFeedbackSubmitted(true);
      setTimeout(() => setFeedbackSubmitted(false), 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  const handleThumbsDown = () => {
    setShowFeedbackModal(true);
  };

  const handleCopyResponse = async () => {
    try {
      await navigator.clipboard.writeText(recommendations);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      
      // Track copy action as feedback
      fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'copy',
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          reason: '',
          comments: 'User copied the AI response'
        })
      }).catch(err => console.error('Error tracking copy:', err));
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleUserQuery = async () => {
    if (!userQuery.trim()) return;
    
    setUserQueryLoading(true);
    try {
      // Combine user query with ticket context for better RAG results
      const ticketContext = ticket.description 
        ? `Ticket: ${ticket.number}\nShort Description: ${ticket.shortDescription}\nDescription: ${ticket.description}\nAssignment Group: ${ticket.assignmentGroup}\n\nUser Query: ${userQuery}`
        : `Ticket: ${ticket.number}\nShort Description: ${ticket.shortDescription}\nAssignment Group: ${ticket.assignmentGroup}\n\nUser Query: ${userQuery}`;
      
      // Call the RAG pipeline to regenerate recommendations based on user query with ticket context
      const response = await searchResolutionSteps(ticketContext);
      
      // Extract resolution steps from response
      const steps = response.resolution_steps || [];
      
      // Convert steps to formatted text
      const formattedRecommendations = steps.map((step, index) => 
        `${index + 1}. ${step.replace(/^[-•*]\s*/, '')}`
      ).join('\n\n');
      
      // Replace recommendations with new RAG response
      setRecommendations(formattedRecommendations || 'No recommendations generated.');
      
      // Update similar tickets if available
      if (response.similar_items) {
        setSimilarTickets(response.similar_items);
      }
      
      // Update related resources if available
      if (response.related_resources) {
        const resources = response.related_resources || [];
        const uniqueResources = [];
        const seenFilenames = new Set();
        
        resources.forEach(resource => {
          if (!seenFilenames.has(resource.filename)) {
            seenFilenames.add(resource.filename);
            uniqueResources.push(resource);
          }
        });
        
        setRelatedResources(uniqueResources);
      }
      
      setUserQuery('');
      setShowUserQueryInput(false);
    } catch (error) {
      console.error('User query error:', error);
      alert('Failed to generate recommendations. Please try again.');
    } finally {
      setUserQueryLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    console.log('Feedback submitted:', {
      ticketId,
      reason: feedbackReason,
      feedback: feedbackText,
      recommendations
    });
    
    try {
      // Send negative feedback to backend
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'thumbs_down',
          reason: feedbackReason,
          comments: feedbackText,
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous'
        })
      });
      
      const result = await response.json();
      console.log('Feedback stored:', result);
      
      setShowFeedbackModal(false);
      setFeedbackText('');
      setFeedbackReason('');
      alert('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  if (!ticket || !ticket.ticketId) return (
    <div className="not-found">
      <h2>Loading ticket details...</h2>
      <Link to="/AgentAssist">Return to Dashboard</Link>
    </div>
  );

  const isResolvedValid = state === 'Resolved' &&
    resolutionNotes.trim() !== '' &&
    resolutionCode.trim() !== '' &&
    resolutionSummary.trim() !== '';

  return (
    <div className="ticket-details-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} />
      
      <div className="ticket-details-content">
        {/* Breadcrumb / Back Link */}
        <Link to="/AgentAssist" className="back-link">
          <FaArrowLeft /> Back to Dashboard
        </Link>

        {/* Ticket Header Card */}
        <div className="ticket-header-card">
          <div className="ticket-header-top">
            <div>
              <h1 className="ticket-id">Ticket #{ticket.ticketId}</h1>
              <p className="ticket-description"><strong>Short Description:</strong> {ticket.shortDescription}</p>
              {ticket.description && (
                <p className="ticket-description"><strong>Description:</strong> {ticket.description}</p>
              )}
            </div>
            <span className={`status-badge status-${ticket.status?.toLowerCase().replace(' ', '-')}`}>
              {ticket.status}
            </span>
          </div>
          
          <div className="ticket-info-grid">
            <div className="info-item">
              <span className="info-label">Received</span>
              <span className="info-value">{ticket.receivedOn}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Assignment Group</span>
              <span className="info-value">{ticket.assignmentGroup}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Assigned To</span>
              <span className="info-value">{ticket.assignedTo}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Item</span>
              <span className="info-value">{ticket.item}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Priority</span>
              <span className={`info-value priority-${ticket.priority?.toLowerCase()}`}>
                {ticket.priority}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="content-grid">
          {/* Left Column: Recommendations */}
          <div className="card recommendations-card">
            <h3 className="card-title">📋 Recommendations</h3>
            {ragLoading && (
              <div className="rag-status">
                <FaSpinner className="spinner" /> Loading AI recommendations...
              </div>
            )}
            {!ragLoading && ragResolutionSteps.length > 0 && (
              <div className="rag-status success">
                ✨ AI-generated recommendations loaded from {similarTickets.length} similar tickets
              </div>
            )}
            <div className="recommendations-content">
              <textarea 
                placeholder="AI-generated recommendations will appear here..."
                rows="15"
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
              />
              {/* Feedback Buttons */}
              {!ragLoading && ragResolutionSteps.length > 0 && (
                <div className="feedback-buttons">
                  <button 
                    className="feedback-btn thumbs-up" 
                    onClick={handleThumbsUp}
                    title="Helpful response"
                  >
                    👍 {feedbackSubmitted && 'Thanks!'}
                  </button>
                  <button 
                    className="feedback-btn thumbs-down" 
                    onClick={handleThumbsDown}
                    title="Not helpful"
                  >
                    👎 Not helpful
                  </button>
                  <button 
                    className="feedback-btn copy-btn" 
                    onClick={handleCopyResponse}
                    title="Copy to clipboard"
                  >
                    📋 {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button 
                    className="feedback-btn userquery-btn" 
                    onClick={() => setShowUserQueryInput(!showUserQueryInput)}
                    title="Ask a question"
                  >
                    💬 User Query
                  </button>
                </div>
              )}
              
              {/* User Query Input Section */}
              {showUserQueryInput && (
                <div className="userquery-section">
                  <textarea 
                    placeholder="Please enter user refined query..."
                    rows="2"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                  />
                  <div className="userquery-actions">
                    <button 
                      className="userquery-send-btn"
                      onClick={handleUserQuery}
                      disabled={userQueryLoading || !userQuery.trim()}
                    >
                      {userQueryLoading ? <><FaSpinner className="spinner" /> Thinking...</> : 'Send'}
                    </button>
                    <button 
                      className="userquery-cancel-btn"
                      onClick={() => {
                        setShowUserQueryInput(false);
                        setUserQuery('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Resolution */}
          <div className="card resolution-card">
            <h3 className="card-title">🎫 Update ticket in ServiceNow</h3>
            
            <div className="dropdown-group">
              <label>State</label>
              <select value={state} onChange={(e) => setState(e.target.value)}>
                <option>In Progress</option>
                <option>On Hold</option>
                <option>Resolved</option>
                <option>Cancelled</option>
              </select>

              {state === 'In Progress' && (
                <>
                  <label>Additional Comments</label>
                  <textarea
                    placeholder="Enter additional comments here..."
                    rows="3"
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                  />
                  
                  <label>Work Notes</label>
                  <textarea
                    placeholder="Enter work notes here..."
                    rows="3"
                    value={workNotes}
                    onChange={(e) => setWorkNotes(e.target.value)}
                  />
                </>
              )}

              {state === 'On Hold' && (
              <>
                <label>On Hold Reason</label>
                <select value={onHoldReason} onChange={(e) => setOnHoldReason(e.target.value)}>
                  <option>--none</option>
                  <option>Awaiting caller</option>
                  <option>Awaiting problem resolution</option>
                  <option>Awaiting vendor</option>
                  <option>Awaiting change</option>
                  <option>Scheduled with caller</option>
                </select>

                {onHoldReason === 'Scheduled with caller' && (
                  <>
                    <label>Schedule Date *</label>
                    <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} required />
                  </>
                )}

                {onHoldReason === 'Awaiting caller' && (
                  <>
                    <label>Additional comments (visible to customer) *</label>
                    <textarea value={additionalComments} onChange={(e) => setAdditionalComments(e.target.value)} required />
                  </>
                )}

                {onHoldReason === 'Awaiting problem resolution' && (
                  <>
                    <label>Problems *</label>
                    <input type="text" value={problemDetails} onChange={(e) => setProblemDetails(e.target.value)} required />
                  </>
                )}

                {onHoldReason === 'Awaiting vendor' && (
                  <>
                    <label>Vendor Ticket *</label>
                    <input type="text" value={vendorTicket} onChange={(e) => setVendorTicket(e.target.value)} required />
                  </>
                )}
              </>
            )}

            {state === 'Resolved' && (
              <>
                <label>Resolution Code *</label>
                <select value={resolutionCode} onChange={(e) => setResolutionCode(e.target.value)}>
                  <option value="">Select</option>
                  <option>Solved (Work around)</option>
                  <option>Solved (permanently)</option>
                  <option>Solved Remotely (work around)</option>
                  <option>Not Solved</option>
                </select>

                <label>Resolution Summary *</label>
                <select value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)}>
                  <option value="">Select</option>
                  <option>Capacity Increased</option>
                  <option>No Action Taken</option>
                  <option>Password Reset</option>
                  <option>Configuration Updated</option>
                </select>
                
                <label>Resolution Notes *</label>
                <textarea
                  placeholder="Enter resolution notes here..."
                  rows="6"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  required
                />
              </>
            )}
          </div>

            <div className="button-group">
              <button className="btn-primary">
                <FaSave /> Update
              </button>
              {state === 'Resolved' && isResolvedValid && (
                <button className="btn-success">
                  <FaCheck /> Resolve
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Resources Panel */}
        <div className="card resources-card">
          <h3 className="card-title">
            <FaFileAlt /> Related Resources
          </h3>
          <div className="tabs">
            <button 
              className={`tab-btn ${activeTab === 'similar' ? 'active' : ''}`}
              onClick={() => setActiveTab('similar')}
            >
              Similar Tickets ({similarTickets.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              Documents ({relatedResources.length})
            </button>
          </div>
          <div className="documents-list">
            {ragLoading && <p>Loading resources...</p>}
            
            {/* Similar Tickets Tab */}
            {!ragLoading && activeTab === 'similar' && (
              <>
                {similarTickets.length > 0 ? (
                  <ul className="resource-list">
                    {similarTickets.map((ticket, index) => {
                      // Clean up ticket number (remove .json extension if present)
                      let ticketNumber = ticket.number || ticket.id || '';
                      ticketNumber = ticketNumber.replace('.json', '');
                      
                      // If still no ticket number, try extracting from URL
                      if (!ticketNumber && ticket.download_url) {
                        const match = ticket.download_url.match(/INC\d+/);
                        if (match) ticketNumber = match[0];
                      }
                      
                      // Always construct ServiceNow URL using the clean ticket number
                      const serviceNowUrl = ticketNumber 
                        ? `https://tranetechnologies.service-now.com/nav_to.do?uri=incident.do?sysparm_query=number=${ticketNumber}`
                        : ticket.download_url;
                      
                      return (
                        <li key={index}>
                          <span>🎫 {ticketNumber}</span>
                          {serviceNowUrl && (
                            <a 
                              href={serviceNowUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-download"
                            >
                              View
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p style={{color: '#666', fontStyle: 'italic'}}>
                    No similar tickets found for this issue.
                  </p>
                )}
              </>
            )}
            
            {/* Documents Tab */}
            {!ragLoading && activeTab === 'documents' && (
              <>
                {relatedResources.length > 0 ? (
                  <ul className="resource-list">
                    {relatedResources.map((resource, index) => (
                      <li key={index}>
                        <span>📄 {resource.filename}</span>
                        {resource.download_url && (
                          <a 
                            href={resource.download_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-download"
                          >
                            View
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{color: '#666', fontStyle: 'italic'}}>
                    No related documents found for this ticket.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="login-bottom-bar">
          <div className="bottom-bar-content">
            <img src={wiproLogo} alt="Wipro" className="bottom-bar-wipro-logo" />
            <p className="bottom-bar-copyright">Copyright © 2026 Wipro LTD. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Help us improve</h3>
            <p>Please let us know what was wrong with the AI response:</p>
            
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
              Reason for feedback:
            </label>
            <select
              value={feedbackReason}
              onChange={(e) => setFeedbackReason(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                fontFamily: 'inherit',
                color: '#2d3748',
                backgroundColor: 'white'
              }}
            >
              <option value="">-- Select a reason --</option>
              <option value="No response received">No response received</option>
              <option value="Response is not relevant">Response is not relevant</option>
              <option value="System performance issue">System performance issue</option>
              <option value="Incomplete information">Incomplete information</option>
              <option value="Incorrect solution">Incorrect solution</option>
              <option value="Other">Other</option>
            </select>
            
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
              Additional comments:
            </label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What could be improved? What was missing?"
              rows="6"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowFeedbackModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitFeedback}
                disabled={!feedbackText.trim()}
                style={{
                  padding: '10px 20px',
                  background: feedbackText.trim() ? 'linear-gradient(135deg, #7B3FF2 0%, #5A2FC2 100%)' : '#cbd5e0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: feedbackText.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailsPage;
